import { IconButton, Button } from '@epilot/concorde-elements'
import { JourneyFeatureFlags } from '@epilot/journey-logic-commons'
import type { Step } from '@epilot/journey-logic-commons/src/types/next'
import classNames from 'classnames'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useAppStoreSelector } from '../../../../../hooks/useAppStoreSelector'
import { useFlag } from '../../../../../hooks/useFlags'
import { navigateBack } from '../../../../../store/actions/navigateBack'
import { selectStepsSorted } from '../../../../../store/selectors/config'
import {
  selectCurrentStepOrThrow,
  selectPreviousStep
} from '../../../../../store/selectors/runtime'

import classes from './MobileStepper.module.scss'

interface MobileStepperProps {
  /**
   * Callback handler to navigate to a given Step
   * @deprecated being sunset by the new logics engine
   */
  onStepClick: (index: number) => void
}

/**
 * Serves the purpose of showing a "stepper" for short and linear Journeys,
 * while on mobile or table.
 */
export const MobileStepper = ({ onStepClick }: MobileStepperProps) => {
  const { t } = useTranslation()
  const previousStep = useAppStoreSelector(selectPreviousStep)
  const currentStep = useAppStoreSelector(selectCurrentStepOrThrow)
  const steps = useAppStoreSelector(selectStepsSorted)
  const newEngineEnabled = useFlag(JourneyFeatureFlags.LOGICS_ENGINE)

  const handleClick = useCallback(
    (step: Step) => {
      // won't navigate forward
      if (step.orderIndex >= currentStep.orderIndex) return

      if (newEngineEnabled) {
        return void navigateBack({ intendedStepId: step.id })
      }

      onStepClick(step.orderIndex)
    },
    [newEngineEnabled, currentStep.orderIndex, onStepClick]
  )

  return (
    <div className={classes.stepperContainer}>
      {previousStep && (
        <IconButton
          aria-label={t('topbar.back_button_label')}
          className={classes.backButton}
          color="primary"
          name="chevron_left"
          onClick={() => handleClick(previousStep)}
        />
      )}

      <div className={classes.dotsContainer} role="tablist">
        {steps.map((step) => {
          const isCurrent = step.id === currentStep.id
          const isActive = step.orderIndex <= currentStep.orderIndex

          return (
            <Button
              aria-controls={`Concorde-Step-Panel-${step.orderIndex + 1}`}
              aria-disabled={!isActive}
              aria-label={t('stepper.navigate_to_step', {
                step: step.orderIndex + 1
              })}
              aria-selected={isCurrent}
              className={classNames(classes.dot, {
                [classes.activeDot]: isActive
              })}
              key={step.id}
              onClick={() => handleClick(step)}
              role="tab"
              variant="bare"
            />
          )
        })}
      </div>
    </div>
  )
}
